import React from 'react';
import { Card, Typography, Chip } from "@material-tailwind/react";

const DailyMatrix = ({ matrixData, format = 'net_amount', loading = false }) => {
    if (loading) {
        return (
            <Card className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                    <div className="space-y-2">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="h-12 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </Card>
        );
    }

    if (!matrixData || !matrixData.dates || matrixData.dates.length === 0) {
        return (
            <Card className="p-6 text-center">
                <Typography variant="h6" color="gray">
                    No data available for the selected week and filters
                </Typography>
            </Card>
        );
    }

    const { dates, cars, matrix_data, column_totals } = matrixData;

    const formatCurrency = (value) => {
        if (!value || value === 0) return '-';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const getCellValue = (date, car) => {
        // Sum all transaction types for this date/car combination
        let total = 0;

        if (matrix_data) {
            Object.values(matrix_data).forEach(transType => {
                const dateRow = transType.matrix.find(row => row.date === date);
                if (dateRow && dateRow[car]) {
                    total += dateRow[car] || 0;
                }
            });
        }

        return total;
    };

    const getCarTotal = (car) => {
        return column_totals ? (column_totals[car] || 0) : 0;
    };

    const getDayTotal = (date) => {
        let total = 0;
        cars.forEach(car => {
            total += getCellValue(date, car);
        });
        return total;
    };

    const getGrandTotal = () => {
        return column_totals ? (column_totals.total || 0) : 0;
    };

    const getCellColor = (value) => {
        if (value > 0) return 'text-green-600 bg-green-50';
        if (value < 0) return 'text-red-600 bg-red-50';
        return 'text-gray-400';
    };

    const formatDateHeader = (dateStr) => {
        const date = new Date(dateStr);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return { dayName, monthDay };
    };

    return (
        <Card className="p-8 shadow-lg border-0 bg-white">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <Typography variant="h5" color="blue-gray" className="font-bold">
                        Daily Performance Matrix
                    </Typography>
                    <Typography variant="small" color="gray" className="mt-1">
                        Comprehensive breakdown of daily transactions and performance
                    </Typography>
                </div>
                <Chip
                    size="md"
                    variant="gradient"
                    color="blue"
                    value={`${format.replace('_', ' ').toUpperCase()}`}
                    className="shadow-md"
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-max table-auto text-left border-collapse">
                    <thead>
                        <tr className="border-b border-blue-gray-100 bg-blue-gray-50">
                            <th className="p-4 sticky left-0 bg-blue-gray-50 z-10 min-w-[150px]">
                                <Typography variant="small" color="blue-gray" className="font-bold">
                                    Car / Date
                                </Typography>
                            </th>
                            {dates.map((date) => {
                                const { dayName, monthDay } = formatDateHeader(date);
                                return (
                                    <th key={date} className="p-4 text-center min-w-[140px]">
                                        <div>
                                            <Typography variant="small" color="blue-gray" className="font-bold">
                                                {dayName}
                                            </Typography>
                                            <Typography variant="small" color="gray" className="text-xs">
                                                {monthDay}
                                            </Typography>
                                        </div>
                                    </th>
                                );
                            })}
                            <th className="p-4 text-center bg-blue-gray-100 min-w-[150px]">
                                <Typography variant="small" color="blue-gray" className="font-bold">
                                    Week Total
                                </Typography>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {cars.map((car, carIndex) => (
                            <tr key={car} className={carIndex % 2 === 0 ? 'bg-blue-gray-50/30' : ''}>
                                <td className="p-4 sticky left-0 bg-white z-10 border-r border-blue-gray-100 min-w-[150px]">
                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                        {car}
                                    </Typography>
                                </td>
                                {dates.map((date) => {
                                    const value = getCellValue(date, car);
                                    return (
                                        <td key={`${car}-${date}`} className="p-4 text-center min-w-[140px]">
                                            <div className={`px-3 py-2 rounded text-sm font-medium ${getCellColor(value)}`}>
                                                {formatCurrency(value)}
                                            </div>
                                        </td>
                                    );
                                })}
                                <td className="p-4 text-center bg-blue-gray-50 font-medium min-w-[150px]">
                                    <div className={`px-3 py-2 rounded text-sm font-bold ${getCellColor(getCarTotal(car))}`}>
                                        {formatCurrency(getCarTotal(car))}
                                    </div>
                                </td>
                            </tr>
                        ))}

                        {/* Daily Totals Row */}
                        <tr className="border-t-2 border-blue-gray-200 bg-blue-gray-100">
                            <td className="p-3 sticky left-0 bg-blue-gray-100 z-10">
                                <Typography variant="small" color="blue-gray" className="font-bold">
                                    Daily Total
                                </Typography>
                            </td>
                            {dates.map((date) => (
                                <td key={`total-${date}`} className="p-3 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold">
                                        {formatCurrency(getDayTotal(date))}
                                    </Typography>
                                </td>
                            ))}
                            <td className="p-4 text-center min-w-[150px]">
                                <Typography variant="small" color="blue-gray" className="font-bold">
                                    {formatCurrency(getGrandTotal())}
                                </Typography>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {cars.length === 0 && (
                <div className="text-center py-8">
                    <Typography variant="h6" color="gray">
                        No cars found for the selected filters
                    </Typography>
                </div>
            )}
        </Card>
    );
};

export default DailyMatrix;
